/**
 * Resolve URLs for static files in /public (stable paths when hosted on cPanel, Netlify, subfolders).
 * Use with homepage "." in package.json — paths must be from the site root, not the current route
 * (e.g. /invoice/add would break ./assets/... if resolved against window.location.href).
 */

/** @returns {string} Path prefix when app is in a subfolder (e.g. /artx); empty at domain root */
function getSiteBasePath() {
  const pub = process.env.PUBLIC_URL ?? '';
  if (!pub || pub === '.') return '';
  return String(pub).replace(/\/$/, '');
}

/**
 * Absolute URL/path from site root — works on any React Router page.
 * @param {string} relativePath - e.g. "assets/invoices/tax-invoice.jpeg"
 */
function resolveFromSiteRoot(relativePath) {
  const clean = String(relativePath || '').replace(/^\//, '');
  const base = getSiteBasePath();
  const combined = [base, clean].filter(Boolean).join('/').replace(/\/+/g, '/');
  const rootPath = combined.startsWith('/') ? combined : `/${combined}`;

  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}${rootPath}`;
  }
  return base ? `${base}/${clean}` : `./${clean}`;
}

/**
 * @param {string} relativePath - e.g. "assets/invoices/tax-invoice.jpeg"
 * @returns {string} URL safe for <img src> and print windows
 */
export function getPublicAssetUrl(relativePath) {
  return resolveFromSiteRoot(relativePath);
}

/** Invoice template images (synced from src/assets via scripts/sync-invoice-assets.js) */
export const INVOICE_PUBLIC_ASSETS = {
  taxHeader: 'assets/invoices/tax-invoice.jpeg',
  proformaHeader: 'assets/invoices/proforma-invoice.png',
  footer: 'assets/invoices/invoice-footer.jpeg',
};

export function getInvoiceHeaderUrl(invoiceType) {
  const isTax = invoiceType === 'tax';
  return getPublicAssetUrl(
    isTax ? INVOICE_PUBLIC_ASSETS.taxHeader : INVOICE_PUBLIC_ASSETS.proformaHeader
  );
}

export function getInvoiceFooterUrl() {
  return getPublicAssetUrl(INVOICE_PUBLIC_ASSETS.footer);
}

/**
 * Webpack-imported file (banner, logos). Works with CRA bundle paths on any host.
 * @param {string} importedModule - default export from `import x from '...'`
 */
export function getBundledAssetUrl(importedModule) {
  if (!importedModule) return '';
  if (/^(https?:|data:|blob:)/i.test(importedModule)) return importedModule;

  const path = String(importedModule).replace(/^\//, '');
  return resolveFromSiteRoot(path);
}
